import React, { useRef, useEffect, useState } from "react";
import aboutGirl from "../assets/about-girl.jpg";
import "../styles/AboutSection.css";

const stats = [
  { icon: "fa-solid fa-user-group",   value: 5000, suffix: "+",    label: "Happy Patients"        },
  { icon: "fa-solid fa-flask",        value: 150,  suffix: "+",    label: "Diagnostic Tests"      },
  { icon: "fa-solid fa-award",        value: 10,   suffix: "+",    label: "Years of Excellence"   },
  { icon: "fa-regular fa-clock",      value: 24,   suffix: " hrs", label: "Quick Report Delivery" },
];

/* ── Animated counter hook ── */
function useCounter(target, duration, active) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(start);
      }
    }, 16);
    return () => clearInterval(timer);
  }, [active, target, duration]);
  return count;
}

/* ── Single stat card with counter ── */
const StatCard = ({ stat, active, delay }) => {
  const count = useCounter(stat.value, 1400, active);
  return (
    <div
      className="ab-stat-card ab-anim-up"
      style={{ transitionDelay: delay }}
    >
      <i className={`${stat.icon} ab-stat-icon`}></i>
      <div className="ab-stat-number">
        {count}{stat.suffix}
      </div>
      <div className="ab-stat-label">{stat.label}</div>
    </div>
  );
};

export default function AboutSection() {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="ab-section" ref={sectionRef}>
      <div className="container-fluid px-4 px-lg-5">

        {/*
          CSS Grid layout with named areas — lets the visual order differ
          per breakpoint without duplicating markup:
            mobile (< 992px): heading → image → body
            desktop (≥ 992px): image on the left, heading + body stacked
                                on the right (original two-column look)
          See .ab-main-row in AboutSection.css.
        */}
        <div className={`ab-main-row ${visible ? "ab-visible" : ""}`}>

          {/* ── Heading: eyebrow + title (first on small screens) ── */}
          <div className="ab-heading-block">
            <span
              className="ab-eyebrow ab-anim-up"
              style={{ transitionDelay: "0.1s" }}
            >
              OUR STORY
            </span>
            <h2
              className="ab-headline ab-anim-up"
              style={{ transitionDelay: "0.2s" }}
            >
              Delivering Excellence in Diagnostics
            </h2>
          </div>

          {/* ── Image (second on small screens) ── */}
          <div className="ab-img-col ab-anim-left">
            <div className="ab-img-wrap">
              <img
                src={aboutGirl}
                alt="Lab scientist at work"
                loading="lazy"
                className="ab-img"
              />
            </div>
          </div>

          {/* ── Body: paragraphs + stat cards (third on small screens) ── */}
          <div className="ab-body-block">
            <div className="ab-paragraphs">
              <p
                className="ab-para ab-anim-up"
                style={{ transitionDelay: "0.3s" }}
              >
                Established with a vision to revolutionize diagnostic healthcare
                in our community, TANBIO Biomed Genomics Laboratory has grown to
                become a trusted name in accurate medical testing.
              </p>
              <p
                className="ab-para ab-anim-up"
                style={{ transitionDelay: "0.38s" }}
              >
                We combine advanced equipment, quality systems and skilled
                professionals to deliver precision results that help doctors
                treat better and patients live healthier lives.
              </p>
            </div>

            <div className="ab-stats-row ab-visible">
              {stats.map((s, i) => (
                <StatCard
                  key={s.label}
                  stat={s}
                  active={visible}
                  delay={`${0.45 + i * 0.1}s`}
                />
              ))}
            </div>
          </div>

        </div>{/* end ab-main-row */}
      </div>{/* end container */}
    </section>
  );
}